import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Stack,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  AccountBalanceWallet as WalletIcon,
  History as HistoryIcon,
  Info as InfoIcon,
  ContentCopy as CopyIcon,
  Key as KeyIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import api from '../api';
import { useAuth } from '../AuthContext';

function WalletManager() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  // State
  const [tabValue, setTabValue] = useState(0);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [alerts, setAlerts] = useState([]);
  
  // Form state
  const [generateForm, setGenerateForm] = useState({
    count: 10,
    purpose: ''
  });
  const [transferForm, setTransferForm] = useState({
    toAddress: '',
    contractAddress: '',
    tokenId: ''
  });

  const showAlert = (message, severity = 'info') => {
    const alert = { id: Date.now(), message, severity };
    setAlerts(prev => [...prev, alert]);
    setTimeout(() => {
      setAlerts(prev => prev.filter(a => a.id !== alert.id));
    }, 5000);
  };

  // Queries
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['walletStats'],
    queryFn: async () => {
      const response = await api.get('/wallets/stats');
      return response.data;
    }
  });

  const { data: walletsData, isLoading: walletsLoading, refetch: refetchWallets } = useQuery({
    queryKey: ['wallets'],
    queryFn: async () => {
      const response = await api.get('/wallets');
      return response.data;
    }
  });

  const { data: batchesData, isLoading: batchesLoading } = useQuery({
    queryKey: ['walletBatches'],
    queryFn: async () => {
      const response = await api.get('/wallets/batches');
      return response.data;
    }
  });

  const { data: transfersData, isLoading: transfersLoading } = useQuery({
    queryKey: ['walletTransfers'],
    queryFn: async () => {
      const response = await api.get('/wallets/transfers');
      return response.data;
    }
  });

  // Mutations
  const generateWalletsMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/wallets/generate', data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['wallets']);
      queryClient.invalidateQueries(['walletStats']);
      queryClient.invalidateQueries(['walletBatches']);
      setGenerateDialogOpen(false);
      setGenerateForm({ count: 10, purpose: '' });
      showAlert(`Successfully generated ${data.generatedCount} wallets!`, 'success');
    },
    onError: (error) => {
      showAlert(error.response?.data?.error || 'Failed to generate wallets', 'error');
    }
  });

  const transferNFTMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/wallets/transfer-nft', data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['walletTransfers']);
      setTransferDialogOpen(false);
      setTransferForm({ toAddress: '', contractAddress: '', tokenId: '' });
      setSelectedWallet(null);
      showAlert('NFT transfer initiated successfully!', 'success');
    },
    onError: (error) => {
      showAlert(error.response?.data?.error || 'Failed to transfer NFT', 'error');
    }
  });

  const handleGenerateWallets = () => {
    generateWalletsMutation.mutate(generateForm);
  };

  const handleTransferNFT = () => {
    if (!selectedWallet) return;
    
    transferNFTMutation.mutate({
      fromWalletId: selectedWallet.id,
      ...transferForm
    });
  };

  const handleViewPrivateKey = async (wallet) => {
    try {
      const response = await api.get(`/wallets/${wallet.id}/private-key`);
      
      // Copy private key directly to clipboard
      await navigator.clipboard.writeText(response.data.privateKey);
      showAlert(`Private key for wallet #${wallet.walletIndex} copied to clipboard`, 'success');
    } catch (error) {
      console.error('Error fetching private key:', error);
      
      let errorMessage = 'Failed to retrieve private key';
      if (error.response?.status === 403) {
        errorMessage = 'Access denied. Only super admins can view private keys.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.name === 'NotAllowedError') {
        errorMessage = 'Clipboard access denied. Please check browser permissions.';
      }
      
      showAlert(errorMessage, 'error');
    }
  };

  // Export admin wallet addresses
  const exportWalletAddresses = async () => {
    try {
      const response = await api.get('/wallets/export-addresses');
      const { exportText, count } = response.data;
      
      // Copy to clipboard
      await navigator.clipboard.writeText(exportText);
      showAlert(`Copied ${count} admin wallet addresses to clipboard`, 'success');
    } catch (error) {
      if (error.name === 'NotAllowedError') {
        showAlert('Clipboard access denied. Please check browser permissions.', 'error');
      } else {
        showAlert(error.response?.data?.error || 'Failed to export wallet addresses', 'error');
      }
    }
  };

  // Export admin wallet addresses with private keys (super admin only)
  const exportWalletAddressesWithKeys = async () => {
    try {
      const response = await api.get('/wallets/export-addresses-with-keys');
      const { exportText, count } = response.data;
      
      // Copy to clipboard
      await navigator.clipboard.writeText(exportText);
      showAlert(`Copied ${count} admin wallet addresses with private keys to clipboard`, 'success');
    } catch (error) {
      if (error.name === 'NotAllowedError') {
        showAlert('Clipboard access denied. Please check browser permissions.', 'error');
      } else {
        showAlert(error.response?.data?.error || 'Failed to export wallet addresses with keys', 'error');
      }
    }
  };



  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showAlert('Copied to clipboard!', 'success');
  };

  const formatAddress = (address) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'pending': return 'warning';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  if (!user || (user.role !== 'admin' && user.role !== 'super_admin')) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Typography variant="h6">Access denied. Admin privileges required.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h4" component="div" flexGrow={1}>
          Wallet Management
        </Typography>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={exportWalletAddresses}
          color="secondary"
        >
          Export Addresses
        </Button>
        {user?.role === 'super_admin' && (
          <Button
            variant="outlined"
            startIcon={<KeyIcon />}
            onClick={exportWalletAddressesWithKeys}
            color="warning"
          >
            Export with Private Keys
          </Button>
        )}
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => {
            refetchWallets();
            queryClient.invalidateQueries(['walletStats']);
          }}
        >
          Refresh
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setGenerateDialogOpen(true)}
        >
          Generate Wallets
        </Button>
      </Stack>

      {/* Alerts */}
      {alerts.map(alert => (
        <Alert key={alert.id} severity={alert.severity} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      ))}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Wallets
              </Typography>
              <Typography variant="h4">
                {statsLoading ? <CircularProgress size={20} /> : stats?.totalWallets || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Wallets
              </Typography>
              <Typography variant="h4">
                {statsLoading ? <CircularProgress size={20} /> : stats?.activeWallets || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Used Wallets
              </Typography>
              <Typography variant="h4">
                {statsLoading ? <CircularProgress size={20} /> : stats?.usedWallets || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total NFTs
              </Typography>
              <Typography variant="h4">
                {statsLoading ? <CircularProgress size={20} /> : stats?.totalNFTs || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab icon={<WalletIcon />} label="Wallets" />
          <Tab icon={<HistoryIcon />} label="Generation History" />
          <Tab icon={<SendIcon />} label="Transfer History" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {tabValue === 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Admin Wallets
          </Typography>
          {walletsLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Index</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>Balance</TableCell>
                    <TableCell>NFTs</TableCell>
                    <TableCell>Last Used</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {walletsData?.wallets?.map((wallet) => (
                    <TableRow key={wallet.id}>
                      <TableCell>#{wallet.walletIndex}</TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="body2" fontFamily="monospace">
                            {formatAddress(wallet.address)}
                          </Typography>
                          <Tooltip title="Copy address">
                            <IconButton size="small" onClick={() => copyToClipboard(wallet.address)}>
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                      <TableCell>{wallet.balance || '0'} ETH</TableCell>
                      <TableCell>{wallet.nftCount || 0}</TableCell>
                      <TableCell>
                        {wallet.lastUsed ? new Date(wallet.lastUsed).toLocaleDateString() : 'Never'}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={wallet.isActive ? 'Active' : 'Inactive'} 
                          color={wallet.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<SendIcon />}
                            onClick={() => {
                              setSelectedWallet(wallet);
                              setTransferDialogOpen(true);
                            }}
                            disabled={!wallet.isActive}
                          >
                            Transfer NFT
                          </Button>
                          {user?.role === 'super_admin' && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              startIcon={<KeyIcon />}
                              onClick={() => handleViewPrivateKey(wallet)}
                            >
                              Copy Private Key
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {tabValue === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Wallet Generation History
          </Typography>
          {batchesLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Requested</TableCell>
                    <TableCell>Generated</TableCell>
                    <TableCell>Purpose</TableCell>
                    <TableCell>Created By</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {batchesData?.map((batch) => (
                    <TableRow key={batch.id}>
                      <TableCell>{new Date(batch.createdAt).toLocaleString()}</TableCell>
                      <TableCell>{batch.requestedCount}</TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography>{batch.generatedCount}</Typography>
                          {batch.requestedCount !== batch.generatedCount && (
                            <Tooltip title={batch.errorMessage || 'Partial generation'}>
                              <InfoIcon fontSize="small" color="warning" />
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>{batch.purpose || '-'}</TableCell>
                      <TableCell>{batch.createdByUsername}</TableCell>
                      <TableCell>
                        <Chip 
                          label={batch.status} 
                          color={getStatusColor(batch.status)}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {tabValue === 2 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            NFT Transfer History
          </Typography>
          {transfersLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>From Wallet</TableCell>
                    <TableCell>To Address</TableCell>
                    <TableCell>Token ID</TableCell>
                    <TableCell>Contract</TableCell>
                    <TableCell>Transaction</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transfersData?.transfers?.map((transfer) => (
                    <TableRow key={transfer.id}>
                      <TableCell>{new Date(transfer.createdAt).toLocaleString()}</TableCell>
                      <TableCell>
                        #{transfer.fromWalletIndex} ({formatAddress(transfer.fromAddress)})
                      </TableCell>
                      <TableCell fontFamily="monospace">
                        {formatAddress(transfer.toAddress)}
                      </TableCell>
                      <TableCell>{transfer.tokenId}</TableCell>
                      <TableCell fontFamily="monospace">
                        {formatAddress(transfer.contractAddress)}
                      </TableCell>
                      <TableCell>
                        {transfer.transactionHash ? (
                          <Tooltip title="Copy transaction hash">
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => copyToClipboard(transfer.transactionHash)}
                            >
                              {formatAddress(transfer.transactionHash)}
                            </Button>
                          </Tooltip>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={transfer.status} 
                          color={getStatusColor(transfer.status)}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Generate Wallets Dialog */}
      <Dialog open={generateDialogOpen} onClose={() => setGenerateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate New Wallets</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Number of Wallets"
              type="number"
              value={generateForm.count}
              onChange={(e) => setGenerateForm(prev => ({ ...prev, count: parseInt(e.target.value) || 1 }))}
              inputProps={{ min: 1, max: 100 }}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Purpose (optional)"
              value={generateForm.purpose}
              onChange={(e) => setGenerateForm(prev => ({ ...prev, purpose: e.target.value }))}
              placeholder="e.g., Expansion for new users, Event rewards"
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleGenerateWallets} 
            variant="contained"
            disabled={generateWalletsMutation.isLoading || generateForm.count < 1 || generateForm.count > 100}
          >
            {generateWalletsMutation.isLoading ? <CircularProgress size={20} /> : `Generate ${generateForm.count} Wallets`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer NFT Dialog */}
      <Dialog open={transferDialogOpen} onClose={() => setTransferDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Transfer NFT from Wallet #{selectedWallet?.walletIndex}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Recipient Address"
              value={transferForm.toAddress}
              onChange={(e) => setTransferForm(prev => ({ ...prev, toAddress: e.target.value }))}
              placeholder="0x..."
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Contract Address"
              value={transferForm.contractAddress}
              onChange={(e) => setTransferForm(prev => ({ ...prev, contractAddress: e.target.value }))}
              placeholder="0x..."
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Token ID"
              value={transferForm.tokenId}
              onChange={(e) => setTransferForm(prev => ({ ...prev, tokenId: e.target.value }))}
              placeholder="123"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleTransferNFT} 
            variant="contained"
            disabled={transferNFTMutation.isLoading || !transferForm.toAddress || !transferForm.contractAddress || !transferForm.tokenId}
          >
            {transferNFTMutation.isLoading ? <CircularProgress size={20} /> : 'Transfer NFT'}
          </Button>
        </DialogActions>
      </Dialog>


    </Box>
  );
}

export default WalletManager;
